import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toastError } from '../utils/toast';
import { processAuthError, useWithTokenRefresh } from '../utils/api';
import { useApi } from '../contexts/ApiContext';
import { AuthTypes, DeviceTypes, HttpError } from '@flashbacktech/flashbackclient/api';
import DeviceFingerprinter from '../services/deviceManager';
import PasswordInput from '../components/PasswordInput';

const AuthPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { handleGoogleSignIn, handleMicrosoftSignIn, handleGithubSignIn, setAuthState } = useAuth();
  const { apiClient } = useApi();

  // État pour gérer les onglets
  const [activeTab, setActiveTab] = useState<'login' | 'signup'>('login');

  // Vérifier les paramètres d'URL pour définir l'onglet actif
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const tabParam = searchParams.get('tab');
    if (tabParam === 'signup') {
      const uid = searchParams.get('uid');
      const token = searchParams.get('token');
      if (uid && token) {
        apiClient.getLink(uid, token).then((response) => {
          if (response.success) {
            localStorage.setItem('signupEmail', response.email);
            setSignupEmail(response.email);
          } else {
            navigate('/');
          }
        });
        localStorage.setItem('activation_uid', uid);
        localStorage.setItem('activation_token', token);
      }
      setActiveTab('signup');
    } else {
      setActiveTab('login');
    }
  }, [location.search, navigate]);

  // États pour le formulaire de connexion
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const withTokenRefresh = useWithTokenRefresh();

  // États pour le formulaire d'inscription
  const [signupEmail, setSignupEmail] = useState('');
  const [signupPassword, setSignupPassword] = useState('');
  const [signupPasswordConfirm, setSignupPasswordConfirm] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [countryCode, setCountryCode] = useState('+33');
  const [contactNumber, setContactNumber] = useState('');

  // État pour gérer les étapes d'inscription
  const [signupStep, setSignupStep] = useState<'credentials' | 'company-info'>('credentials');

  // État de chargement
  const [loading, setLoading] = useState(false);

  // Fonction de gestion de la connexion
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const deviceFingerprinter = DeviceFingerprinter.getInstance();
      const deviceInfo: DeviceTypes.DeviceInfo = await deviceFingerprinter.getDeviceInfoForAuth();

      const response = await withTokenRefresh(
        async () =>
          await apiClient.userLogin({
            email: loginEmail,
            password: loginPassword,
            deviceInfo,
          })
      );
      if (response.accessToken && response.refreshToken && response.user) {
        try {
          localStorage.setItem('loginProvider', 'local');
          localStorage.removeItem('activation_uid');
          localStorage.removeItem('activation_token');
          localStorage.removeItem('signupEmail');
        } catch (_err) {
          /* noop: ignore storage errors */
        }
        setAuthState({
          user: {
            email: response.user.email,
            name: response.user.name,
            imageUrl: '',
          },
          token: response.accessToken,
          accessToken: response.accessToken || null,
          refreshToken: response.refreshToken || null,
          expiresAt: response.expiresAt || null,
        });
        navigate('/hub');
      } else {
        toastError(response.message || 'Échec de la connexion');
      }
    } catch (error) {
      toastError(error instanceof Error ? error.message : String(error) || 'Login failed', 10000);
    } finally {
      setLoading(false);
    }
  };

  // Fonction pour passer à l'étape suivante de l'inscription
  const handleNextStep = (e: React.FormEvent) => {
    e.preventDefault();

    // Validation basique
    if (!signupEmail || !signupPassword || !signupPasswordConfirm) {
      toastError('Please fill in all required fields');
      return;
    }

    // Vérification du mot de passe (au moins 8 caractères et un caractère spécial)
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(signupPassword);
    if (signupPassword.length < 8) {
      toastError('Password must be at least 8 characters');
      return;
    }
    if (!hasSpecialChar) {
      toastError('Password must include at least one special character');
      return;
    }

    // Vérification que les mots de passe correspondent
    if (signupPassword !== signupPasswordConfirm) {
      toastError('Passwords do not match');
      return;
    }

    setSignupStep('company-info');
  };

  // Fonction de gestion de l'inscription finale
  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    let response: any;
    try {
      // S'assurer que tous les champs obligatoires sont remplis
      if (!firstName || !lastName) {
        toastError('First name and last name are required');
        setLoading(false);
        return;
      }

      // Créer l'objet userData avec les champs requis
      const activationUid = localStorage.getItem('activation_uid');
      const activationToken = localStorage.getItem('activation_token');
      const activationEmail = localStorage.getItem('signupEmail');

      if (!activationEmail || activationEmail !== signupEmail) {
        toastError('The email you are trying to use is not the one you received the activation link for. Please request a new activation link.', 10000);
        setLoading(false);
        return;
      }

      const userData: AuthTypes.RegisterBody = {
        email: signupEmail,
        password: signupPassword,
        firstName,
        lastName,
        phone: contactNumber ? `${countryCode}${contactNumber}` : '',
        isBusiness: false,
        activationUid: activationUid || undefined,
        activationToken: activationToken || undefined,
        companyName: `${firstName} ${lastName}`,
      };

      response = await withTokenRefresh(async () => await apiClient.userRegister(userData));
      if (response.accessToken && response.refreshToken && response.user) {
        try {
          localStorage.setItem('loginProvider', 'local');
          localStorage.removeItem('activation_uid');
          localStorage.removeItem('activation_token');
          localStorage.removeItem('signupEmail');
        } catch (_err) {
          /* noop: ignore storage errors */
        }
        
        navigate(`/verify-email?uid=${response.user.id}`);
      } else {
        toastError(response.message || 'Failed to create account');
      }
    } catch (error) {
      // Check if it's an HttpError (either by instanceof or by shape)
      const isHttpError = 
        (error instanceof HttpError) ||
        (error && typeof error === 'object' && 'status' in error && 'data' in error);

      if (isHttpError) {
        const httpError = error as any;
        const status = httpError.status;
        const errorCode = httpError.data?.error_code;
        
        if (status === 400 || status === 401 || status === 403) {
          const errorMessage = processAuthError(errorCode);
          toastError(errorMessage);
        }
      } else {
        toastError(error instanceof Error ? error.message : String(error) || 'Signup failed');
      }
    } finally {
      setLoading(false);
    }
  };

  const hasActivationLink =
    typeof window !== 'undefined' &&
    !!localStorage.getItem('activation_uid') &&
    !!localStorage.getItem('activation_token');

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 px-4 sm:px-6 py-12">
      <div className="mb-6">
        <img
          src="/flashlogoblack.svg"
          alt="Flashback Logo"
          className="h-6 sm:h-8"
          onClick={() => navigate('/')}
          style={{ cursor: 'pointer' }}
        />
      </div>

      <div className="w-full max-w-[360px] mx-auto">
        <h2
          className="text-center mb-4"
          style={{
            fontSize: '30px',
            fontWeight: 600,
            lineHeight: '38px',
            letterSpacing: '0%',
          }}
        >
          {activeTab === 'login'
            ? 'Log in to your account'
            : signupStep === 'credentials'
            ? 'Create an account'
            : 'Tell us about yourself'}
        </h2>
        <p className="text-center text-gray-600 mb-8">
          {activeTab === 'login'
            ? 'Welcome back! Please enter your details.'
            : 'Get started for free.'}
        </p>

        {/* Onglets - masqués lorsqu'on est sur la deuxième partie du signup */}
        {!(activeTab === 'signup' && signupStep === 'company-info') && (
          <div
            className="flex mb-6 bg-[#FAFAFA]"
            style={{
              borderRadius: '8px',
              height: '36px',
              border: '1px solid #E9EAEB',
              padding: '2px',
            }}
          >
            <button
              className={`flex-1 text-center font-medium transition-all flex items-center justify-center ${
                activeTab === 'login' ? 'bg-white text-black' : 'text-gray-500'
              }`}
              style={{
                borderRadius: activeTab === 'login' ? '6px' : '0',
                height: '100%',
                border: activeTab === 'login' ? '1px solid #E5E7EB' : 'none',
                margin: '0',
              }}
              onClick={() => setActiveTab('login')}
            >
              Log in
            </button>
            <button
              className={`flex-1 text-center font-medium transition-all flex items-center justify-center ${
                activeTab === 'signup' ? 'bg-white text-black' : 'text-gray-500'
              }`}
              style={{
                borderRadius: activeTab === 'signup' ? '6px' : '0',
                height: '100%',
                border: activeTab === 'signup' ? '1px solid #E5E7EB' : 'none',
                margin: '0',
              }}
              onClick={() => setActiveTab('signup')}
            >
              Sign up
            </button>
          </div>
        )}

        {/* Contenu des onglets */}
        <div>
          {activeTab === 'login' ? (
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label
                  htmlFor="login-email"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Email
                </label>
                <input
                  type="email"
                  id="login-email"
                  value={loginEmail}
                  onChange={(e) => setLoginEmail(e.target.value)}
                  placeholder="Enter your email"
                  className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-[#617BFF]"
                  required
                />
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                  Password
                </label>
                <div className="relative">
                  <input
                    type={showLoginPassword ? 'text' : 'password'}
                    id="password"
                    value={loginPassword}
                    onChange={(e) => setLoginPassword(e.target.value)}
                    placeholder="••••••••"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-[#617BFF] pr-10"
                    required
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowLoginPassword(!showLoginPassword)}
                  >
                    {showLoginPassword ? (
                      <svg
                        className="h-5 w-5 text-gray-400"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
                        />
                      </svg>
                    ) : (
                      <svg
                        className="h-5 w-5 text-gray-400"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                        />
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <input
                    id="remember-me"
                    name="remember-me"
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    className="h-4 w-4 text-[#617BFF] focus:ring-[#617BFF] border-gray-300 rounded"
                  />
                  <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-700">
                    Remember for 30 days
                  </label>
                </div>
                <Link
                  to="/forgot-password"
                  className="text-sm font-medium text-[#617BFF] hover:text-[#4a62cc]"
                >
                  Forgot password
                </Link>
              </div>

              <button
                type="submit"
                className="flex justify-center items-center py-2 w-full border border-transparent rounded-md bg-[#617BFF] text-white hover:bg-[#4a62cc] mb-3"
                style={{ color: '#FFFFFF', fontWeight: 600, fontSize: '16px', lineHeight: '24px' }}
                disabled={loading}
              >
                {loading ? 'Processing...' : 'Sign in'}
              </button>

              <div className="space-y-3 mb-6">
                <button
                  type="button"
                  onClick={handleGoogleSignIn}
                  className="flex justify-center items-center py-2 w-full border border-gray-300 rounded-md shadow-sm bg-white hover:bg-gray-50"
                  style={{
                    color: '#414651',
                    fontWeight: 600,
                    fontSize: '16px',
                    lineHeight: '24px',
                  }}
                >
                  <svg className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2" viewBox="0 0 24 24">
                    <path
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                      fill="#4285F4"
                    />
                    <path
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      fill="#34A853"
                    />
                    <path
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                      fill="#FBBC05"
                    />
                    <path
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                      fill="#EA4335"
                    />
                  </svg>
                  Sign in with Google
                </button>
                <button
                  type="button"
                  onClick={handleGithubSignIn}
                  className="flex justify-center items-center py-2 w-full border border-gray-300 rounded-md shadow-sm bg-white hover:bg-gray-50"
                  style={{
                    color: '#414651',
                    fontWeight: 600,
                    fontSize: '16px',
                    lineHeight: '24px',
                  }}
                >
                  <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M12 2C6.477 2 2 6.477 2 12c0 4.42 2.87 8.17 6.84 9.5.5.08.66-.23.66-.5v-1.69c-2.77.6-3.36-1.34-3.36-1.34-.46-1.16-1.11-1.47-1.11-1.47-.91-.62.07-.6.07-.6 1 .07 1.53 1.03 1.53 1.03.87 1.52 2.34 1.07 2.91.83.09-.65.35-1.09.63-1.34-2.22-.25-4.55-1.11-4.55-4.92 0-1.11.38-2 1.03-2.71-.1-.25-.45-1.29.1-2.64 0 0 .84-.27 2.75 1.02.79-.22 1.65-.33 2.5-.33.85 0 1.71.11 2.5.33 1.91-1.29 2.75-1.02 2.75-1.02.55 1.35.2 2.39.1 2.64.65.71 1.03 1.6 1.03 2.71 0 3.82-2.34 4.66-4.57 4.91.36.31.69.92.69 1.85V21c0 .27.16.59.67.5C19.14 20.16 22 16.42 22 12A10 10 0 0012 2z"
                    />
                  </svg>
                  Sign in with Github
                </button>
                <button
                  type="button"
                  onClick={handleMicrosoftSignIn}
                  className="flex justify-center items-center py-2 w-full border border-gray-300 rounded-md shadow-sm bg-white hover:bg-gray-50"
                  style={{
                    color: '#414651',
                    fontWeight: 600,
                    fontSize: '16px',
                    lineHeight: '24px',
                  }}
                >
                  <svg className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2" viewBox="0 0 23 23">
                    <path fill="#f35325" d="M1 1h10v10H1z" />
                    <path fill="#81bc06" d="M12 1h10v10H12z" />
                    <path fill="#05a6f0" d="M1 12h10v10H1z" />
                    <path fill="#ffba08" d="M12 12h10v10H12z" />
                  </svg>
                  Sign in with Microsoft
                </button>
              </div>
              {activeTab === 'login' && (
                <div className="text-center mt-6">
                  <p className="text-sm text-gray-600">
                    Don't have an account?{' '}
                    <button
                      onClick={() => setActiveTab('signup')}
                      className="font-medium text-[#617BFF] hover:text-[#4a62cc]"
                    >
                      Sign up
                    </button>
                  </p>
                </div>
              )}
            </form>
          ) : !hasActivationLink ? (
            <div className="space-y-4 text-center py-6">
              <p className="text-gray-600">
                Sign up requires an activation link. Please request one from the homepage.
              </p>
              <button
                type="button"
                onClick={() => navigate('/')}
                className="text-[#617BFF] font-medium hover:text-[#4a62cc]"
              >
                Go to homepage
              </button>
              <div className="mt-6">
                <span className="text-sm text-gray-600">
                  Already have an account?{' '}
                  <button
                    type="button"
                    onClick={() => setActiveTab('login')}
                    className="font-medium text-[#617BFF] hover:text-[#4a62cc]"
                  >
                    Log in
                  </button>
                </span>
              </div>
            </div>
          ) : (
            <div>
              {signupStep === 'credentials' ? (
                // Étape 1 : Informations de base

                <form onSubmit={handleNextStep} className="space-y-4">
                  <div className="space-y-3 mb-6">
                    <button
                      type="button"
                      onClick={handleGoogleSignIn}
                      className="flex justify-center items-center py-2 w-full border border-gray-300 rounded-md shadow-sm bg-white hover:bg-gray-50"
                      style={{
                        color: '#414651',
                        fontWeight: 600,
                        fontSize: '16px',
                        lineHeight: '24px',
                      }}
                    >
                      <svg className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2" viewBox="0 0 24 24">
                        <path
                          d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                          fill="#4285F4"
                        />
                        <path
                          d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                          fill="#34A853"
                        />
                        <path
                          d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                          fill="#FBBC05"
                        />
                        <path
                          d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                          fill="#EA4335"
                        />
                      </svg>
                      Sign up with Google
                    </button>
                    <button
                      type="button"
                      onClick={handleMicrosoftSignIn}
                      className="flex justify-center items-center py-2 w-full border border-gray-300 rounded-md shadow-sm bg-white hover:bg-gray-50"
                      style={{
                        color: '#414651',
                        fontWeight: 600,
                        fontSize: '16px',
                        lineHeight: '24px',
                      }}
                    >
                      <svg className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2" viewBox="0 0 23 23">
                        <path fill="#f35325" d="M1 1h10v10H1z" />
                        <path fill="#81bc06" d="M12 1h10v10H12z" />
                        <path fill="#05a6f0" d="M1 12h10v10H1z" />
                        <path fill="#ffba08" d="M12 12h10v10H12z" />
                      </svg>
                      Sign up with Microsoft
                    </button>
                    <button
                      type="button"
                      onClick={handleGithubSignIn}
                      className="flex justify-center items-center py-2 w-full border border-gray-300 rounded-md shadow-sm bg-white hover:bg-gray-50"
                      style={{
                        color: '#414651',
                        fontWeight: 600,
                        fontSize: '16px',
                        lineHeight: '24px',
                      }}
                    >
                      <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M12 2C6.477 2 2 6.477 2 12c0 4.42 2.87 8.17 6.84 9.5.5.08.66-.23.66-.5v-1.69c-2.77.6-3.36-1.34-3.36-1.34-.46-1.16-1.11-1.47-1.11-1.47-.91-.62.07-.6.07-.6 1 .07 1.53 1.03 1.53 1.03.87 1.52 2.34 1.07 2.91.83.09-.65.35-1.09.63-1.34-2.22-.25-4.55-1.11-4.55-4.92 0-1.11.38-2 1.03-2.71-.1-.25-.45-1.29.1-2.64 0 0 .84-.27 2.75 1.02.79-.22 1.65-.33 2.5-.33.85 0 1.71.11 2.5.33 1.91-1.29 2.75-1.02 2.75-1.02.55 1.35.2 2.39.1 2.64.65.71 1.03 1.6 1.03 2.71 0 3.82-2.34 4.66-4.57 4.91.36.31.69.92.69 1.85V21c0 .27.16.59.67.5C19.14 20.16 22 16.42 22 12A10 10 0 0012 2z"
                        />
                      </svg>
                      Sign up with Github
                    </button>
                  </div>
                   
                  <div className="relative my-4">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-gray-300"></div>
                    </div>
                    <div className="relative flex justify-center text-sm">
                      <span className="px-2 bg-white text-gray-500">OR</span>
                    </div>
                  </div>

                  <div>
                    <label
                      htmlFor="signup-email"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Email
                    </label>
                    <input
                      type="email"
                      id="signup-email"
                      value={signupEmail}
                      onChange={(e) => setSignupEmail(e.target.value)}
                      placeholder="Enter your email"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-[#617BFF]"
                      required
                    />
                  </div>

                  <PasswordInput
                    password={signupPassword}
                    passwordConfirm={signupPasswordConfirm}
                    onPasswordChange={setSignupPassword}
                    onPasswordConfirmChange={setSignupPasswordConfirm}
                  />

                  <button
                    type="submit"
                    className="w-full bg-[#617BFF] text-white py-2 sm:py-2.5 rounded-md hover:bg-[#4a62cc] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#617BFF]"
                    style={{
                      color: '#FFFFFF',
                      fontWeight: 600,
                      fontSize: '16px',
                      lineHeight: '24px',
                    }}
                  >
                    Get started
                  </button>

                  <div className="mt-4 text-center">
                    <span className="text-sm text-gray-600">
                      Already have an account?{' '}
                      <button
                        type="button"
                        onClick={() => setActiveTab('login')}
                        className="text-[#617BFF] hover:text-[#4a62cc] font-medium"
                      >
                        Log in
                      </button>
                    </span>
                  </div>
                </form>
              ) : (
                // Étape 2 : Informations personnelles
                <form onSubmit={handleSignup} className="space-y-4">
                  <div className="mb-4">
                    <h3 className="text-lg font-medium text-gray-900 mb-1">
                      Personal Information
                    </h3>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label
                        htmlFor="first-name"
                        className="block text-sm font-medium text-gray-700 mb-1"
                      >
                        First name(s)
                      </label>
                      <input
                        type="text"
                        id="first-name"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        placeholder="First name(s)"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-[#617BFF]"
                        required
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="last-name"
                        className="block text-sm font-medium text-gray-700 mb-1"
                      >
                        Last name
                      </label>
                      <input
                        type="text"
                        id="last-name"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        placeholder="Last name"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-[#617BFF]"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label
                      htmlFor="contact-number"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Contact number
                    </label>
                    <div className="flex">
                      <select
                        className="w-28 px-2 py-2 border border-gray-300 rounded-l-md focus:outline-none focus:ring-1 focus:ring-[#617BFF] bg-gray-50 text-sm"
                        aria-label="Country code"
                        value={countryCode}
                        onChange={(e) => setCountryCode(e.target.value)}
                        style={{ flexShrink: 0 }}
                      >
                        <option value="+33">+33 🇫🇷</option>
                        <option value="+1">+1 🇺🇸</option>
                        <option value="+44">+44 🇬🇧</option>
                        <option value="+49">+49 🇩🇪</option>
                        <option value="+39">+39 🇮🇹</option>
                        <option value="+34">+34 🇪🇸</option>
                        <option value="+32">+32 🇧🇪</option>
                        <option value="+41">+41 🇨🇭</option>
                        <option value="+352">+352 🇱🇺</option>
                      </select>
                      <input
                        type="tel"
                        id="contact-number"
                        value={contactNumber}
                        onChange={(e) => setContactNumber(e.target.value)}
                        placeholder="Enter a contact number"
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-r-md focus:outline-none focus:ring-1 focus:ring-[#617BFF] border-l-0"
                        required
                      />
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-3 mt-2">
                    <button
                      type="button"
                      onClick={() => setSignupStep('credentials')}
                      className="w-full sm:w-auto px-6 py-2 sm:py-2.5 text-sm sm:text-base border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400 transition-colors"
                    >
                      ← Back
                    </button>
                    <button
                      type="submit"
                      className="flex-1 bg-[#617BFF] text-white py-2 sm:py-2.5 text-sm sm:text-base rounded-md hover:bg-[#4a62cc] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#617BFF] transition-colors"
                      disabled={loading}
                    >
                      {loading ? 'Processing...' : 'Finish registration'}
                    </button>
                  </div>
                </form>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuthPage;
